import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { Centipede } from '../entities/Centipede';
import { Spider } from '../entities/Spider';
import { Flea } from '../entities/Flea';

export class EnemyManager {
  private gameWidth: number;
  private gameHeight: number;
  private gridSize: number;
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  
  private centipedes: Centipede[] = [];
  private spiders: Spider[] = [];
  private fleas: Flea[] = [];
  
  // Spawn timers
  private spiderSpawnTimer: number = 0;
  private fleaSpawnTimer: number = 0;
  private spiderSpawnInterval: number = 15; // Spider every 15 seconds
  private fleaSpawnInterval: number = 20; // Flea every 20 seconds
  
  // Wave configuration
  private currentWave: number = 1;
  private centipedeSpeed: number = 60;
  private maxSpiders: number = 1;
  private maxFleas: number = 2;

  constructor(
    gameWidth: number,
    gameHeight: number,
    gridSize: number,
    renderManager: RenderManager,
    audioManager: AudioManager
  ) {
    this.gameWidth = gameWidth;
    this.gameHeight = gameHeight;
    this.gridSize = gridSize;
    this.renderManager = renderManager;
    this.audioManager = audioManager;
  }

  public initializeWave(waveNumber: number, speed: number): void {
    this.currentWave = waveNumber;
    this.centipedeSpeed = speed;
    
    // Clear existing enemies
    this.centipedes = [];
    this.spiders = [];
    this.fleas = [];
    
    // Reset spawn timers
    this.spiderSpawnTimer = 0;
    this.fleaSpawnTimer = 0;
    
    // Adjust difficulty based on wave
    this.maxSpiders = Math.min(3, Math.floor(waveNumber / 2) + 1);
    this.maxFleas = Math.min(4, Math.floor(waveNumber / 3) + 1);
    this.spiderSpawnInterval = Math.max(5, 15 - waveNumber);
    this.fleaSpawnInterval = Math.max(8, 20 - waveNumber);
    
    // Create initial centipede(s)
    this.createInitialCentipede(waveNumber);
    
    console.log(`Wave ${waveNumber}: Centipedes: ${this.centipedes.length}, Max Spiders: ${this.maxSpiders}, Max Fleas: ${this.maxFleas}`);
  }

  private createInitialCentipede(waveNumber: number): void {
    // Main centipede starts at top of screen
    const segmentCount = Math.max(8, 12 - Math.floor(waveNumber / 2)); // Start with 12 segments, decrease over time
    const startX = this.gameWidth / 2;
    const startY = this.gridSize;
    
    const centipede = new Centipede(
      `centipede_wave_${waveNumber}_main`,
      startX,
      startY,
      segmentCount,
      this.centipedeSpeed,
      this.renderManager,
      this.audioManager,
      this.gameWidth,
      this.gameHeight
    );
    
    this.centipedes.push(centipede);
    
    // Add additional centipedes for higher waves
    if (waveNumber > 3) {
      const additionalCount = Math.floor((waveNumber - 3) / 2);
      for (let i = 0; i < additionalCount; i++) {
        const additionalX = (i + 1) * (this.gameWidth / (additionalCount + 2));
        const additionalY = this.gridSize * (2 + i);
        const additionalSegments = Math.max(4, 8 - i);
        
        const additionalCentipede = new Centipede(
          `centipede_wave_${waveNumber}_${i}`,
          additionalX,
          additionalY,
          additionalSegments,
          this.centipedeSpeed + (i * 10),
          this.renderManager,
          this.audioManager,
          this.gameWidth,
          this.gameHeight
        );
        
        this.centipedes.push(additionalCentipede);
      }
    }
  }

  public update(
    deltaTime: number, 
    existingCentipedes: Centipede[], 
    existingSpiders: Spider[], 
    existingFleas: Flea[]
  ): void {
    // Update spawn timers
    this.spiderSpawnTimer += deltaTime;
    this.fleaSpawnTimer += deltaTime;
    
    // Update our internal arrays with existing enemies
    this.centipedes = existingCentipedes;
    this.spiders = existingSpiders;
    this.fleas = existingFleas;
    
    // Spawn spider if needed
    if (this.shouldSpawnSpider()) {
      this.spawnSpider();
    }
    
    // Spawn flea if needed
    if (this.shouldSpawnFlea()) {
      this.spawnFlea();
    }
  }

  private shouldSpawnSpider(): boolean {
    return this.spiderSpawnTimer >= this.spiderSpawnInterval && 
           this.spiders.filter(s => s.active).length < this.maxSpiders;
  }

  private shouldSpawnFlea(): boolean {
    return this.fleaSpawnTimer >= this.fleaSpawnInterval && 
           this.fleas.filter(f => f.active).length < this.maxFleas;
  }

  private spawnSpider(): void {
    // Spider can spawn from either side of the screen
    const spawnSide = Math.random() < 0.5 ? 'left' : 'right';
    const startX = spawnSide === 'left' ? 0 : this.gameWidth - 16;
    const startY = Math.random() * (this.gameHeight * 0.6) + (this.gameHeight * 0.2); // Middle 60% of screen
    
    const spider = new Spider(
      `spider_${Date.now()}`,
      startX,
      startY,
      this.renderManager,
      this.audioManager,
      this.gameWidth,
      this.gameHeight
    );
    
    this.spiders.push(spider);
    this.spiderSpawnTimer = 0;
    
    console.log(`Spawned spider at (${startX}, ${startY})`);
  }

  private spawnFlea(): void {
    // Flea spawns from top of screen
    const startX = Math.random() * (this.gameWidth - 32) + 16; // Avoid edges
    const startY = -16; // Start above screen
    
    const flea = new Flea(
      `flea_${Date.now()}`,
      startX,
      startY,
      this.renderManager,
      this.audioManager,
      this.gameHeight
    );
    
    this.fleas.push(flea);
    this.fleaSpawnTimer = 0;
    
    console.log(`Spawned flea at (${startX}, ${startY})`);
  }

  public getCentipedes(): Centipede[] {
    return this.centipedes;
  }

  public getSpiders(): Spider[] {
    return this.spiders;
  }

  public getFleas(): Flea[] {
    return this.fleas;
  }

  public isWaveComplete(): boolean {
    const activeCentipedes = this.centipedes.filter(c => c.active);
    return activeCentipedes.length === 0;
  }

  public getActiveEnemyCount(): number {
    const activeCentipedes = this.centipedes.filter(c => c.active).length;
    const activeSpiders = this.spiders.filter(s => s.active).length;
    const activeFleas = this.fleas.filter(f => f.active).length;
    
    return activeCentipedes + activeSpiders + activeFleas;
  }
}